/**
 * UNIFIED FINANCIAL DATA MODELS
 * 
 * This file contains the standardized data models for all financial calculations
 * in the Proporio application. These models ensure temporal consistency and
 * follow international real estate conventions.
 * 
 * Key Principles:
 * - All financial amounts MUST specify their time period 
 * - All calculations use standardized variable definitions
 * - Data validation and quality scoring built-in
 * - Temporal conversion utilities included
 */

export interface TemporalFinancialAmount {
  amount: number;
  currency: string;
  period: 'monthly' | 'quarterly' | 'annually';
  effectiveDate: Date;
  source: 'actual' | 'estimated' | 'projected';
  confidence: number; // 0-1 scale (1 = actual data, 0.5 = estimated)
}

/**
 * Helper function to create temporal amounts
 */
export function createTemporalAmount(
  amount: number,
  period: 'monthly' | 'quarterly' | 'annually',
  currency = 'EUR',
  source: 'actual' | 'estimated' | 'projected' = 'actual',
  confidence = 1.0
): TemporalFinancialAmount {
  return {
    amount,
    currency,
    period,
    effectiveDate: new Date(),
    source,
    confidence
  };
}

/**
 * Date period for time-based calculations
 */
export interface DatePeriod {
  startDate: Date;
  endDate: Date;
  label?: string;
}

/**
 * STANDARDIZED property financial data
 * Replaces multiple inconsistent PropertyFinancials interfaces
 */
export interface UnifiedPropertyFinancials {
  propertyId: string;
  
  // Property Value (always annual for valuation)
  currentValue: TemporalFinancialAmount;
  purchasePrice: TemporalFinancialAmount;
  purchaseDate?: Date; // CRITICAL: Used to calculate pro-rata for first year
  
  // Income (ALWAYS monthly with clear growth rates)
  baseRent: TemporalFinancialAmount; // MUST be monthly (may include fallbacks/estimates)
  actualContractRent?: TemporalFinancialAmount; // REAL contract rent ONLY (0 if vacant) - for portfolio aggregation
  additionalRent?: TemporalFinancialAmount; // MUST be monthly
  otherIncome?: TemporalFinancialAmount; // MUST be monthly
  
  // Operating Expenses (ALWAYS annual with clear breakdown)
  propertyTax: TemporalFinancialAmount; // MUST be annual
  insurance: TemporalFinancialAmount; // MUST be annual
  maintenance: TemporalFinancialAmount; // MUST be annual
  managementFee: TemporalFinancialAmount; // Can be annual or percentage
  utilities: TemporalFinancialAmount; // MUST be annual
  otherExpenses: TemporalFinancialAmount; // MUST be annual
  
  // Growth Rates (annual percentages)
  rentGrowthRate: number; // Annual percentage
  expenseGrowthRate: number; // Annual percentage
  propertyGrowthRate: number; // Annual percentage
  
  // Vacancy & Occupancy
  vacancyRate: number; // Percentage (0-100)
  occupancyHistory: OccupancyRecord[];
  
  // Data Quality
  dataQuality: 'high' | 'medium' | 'low';
  lastUpdated: Date;
  calculatedBy: string; // Service that calculated this
}

export interface OccupancyRecord {
  period: DatePeriod;
  occupancyRate: number;
  reason?: string;
}

/**
 * STANDARDIZED contract financial data
 */
export interface UnifiedContractFinancials {
  contractId: string;
  propertyId: string;
  
  // Rent (ALWAYS monthly)
  monthlyBaseRent: TemporalFinancialAmount;
  monthlyAdditionalCharges?: TemporalFinancialAmount;
  securityDeposit: TemporalFinancialAmount;
  
  // Contract Terms
  startDate: Date;
  endDate: Date;
  renewalTerms: RenewalTerms;
  indexationRules: IndexationRule[];
  
  // Payment Terms
  paymentFrequency: 'monthly' | 'quarterly' | 'annually';
  paymentDay: number;
  
  // Tenant Expense Responsibilities
  tenantPaysExpenses: {
    propertyTax: boolean;
    insurance: boolean;
    utilities: boolean;
    maintenance: boolean;
    commonCharges: boolean;
  };
  
  // Data Quality
  dataQuality: 'high' | 'medium' | 'low';
  lastUpdated: Date;
}

export interface RenewalTerms {
  automaticRenewal: boolean;
  renewalPeriod?: number; // months
  noticeRequired?: number; // days
  rentIncrease?: number; // percentage
}

export interface IndexationRule {
  type: 'fixed' | 'cpi' | 'custom';
  frequency: 'annual' | 'biannual';
  value?: number; // percentage or fixed amount
  effectiveDate: Date;
}

/**
 * STANDARDIZED loan financial data
 */
export interface UnifiedLoanFinancials {
  loanId: string;
  propertyId: string;
  
  // Principal
  originalAmount: TemporalFinancialAmount;
  currentBalance: TemporalFinancialAmount;
  
  // Terms
  interestRate: number; // Annual percentage
  termYears: number;
  paymentFrequency: 'monthly' | 'quarterly' | 'annually';
  
  // Payments (period-specific based on paymentFrequency)
  regularPayment: TemporalFinancialAmount;
  principalPayment: TemporalFinancialAmount;
  interestPayment: TemporalFinancialAmount;
  
  // Schedule
  startDate: Date;
  maturityDate: Date;
  amortizationSchedule: LoanPaymentSchedule[];
  
  // Costs
  originationFees: TemporalFinancialAmount;
  monthlyFees?: TemporalFinancialAmount;
  
  // Type & Status
  loanType: 'fixed' | 'variable' | 'interest-only';
  status: 'active' | 'completed' | 'default';
  
  // Data Quality
  dataQuality: 'high' | 'medium' | 'low';
  lastUpdated: Date;
}

export interface LoanPaymentSchedule {
  paymentNumber: number;
  paymentDate: Date;
  principalPayment: number;
  interestPayment: number;
  totalPayment: number;
  remainingBalance: number;
}

/**
 * Data validation result interface
 */
export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  normalizedData?: TemporalFinancialAmount;
}

/**
 * Data quality assessment result
 */
export interface DataQualityResult {
  score: number; // 0-1 scale
  quality: 'high' | 'medium' | 'low';
  issues: string[];
  recommendations: string[];
  confidenceFactors: string[];
} 